import { useEffect, useState } from 'react'
import {
  erapRefLabel,
  fetchErapCompanies,
  fetchErapProtocolsSummary,
  fetchErapTransport,
  fetchErapTransportSummary,
  type ErapProtocolsSummary,
  type ErapTransportSummary,
} from '@/services/erapApi'

/*
 * Все данные домена «Правонарушения» витрины — для `/admin-violations-v2`.
 *
 * ФОРМА `articleGroupStats` СОХРАНЕНА НАМЕРЕННО: модуль разбирает объект с
 * полем `rows`, где у каждой строки есть `name_ru`, `current.total_protocols`
 * и `previous.total_protocols`. Отдать данные в этой же форме дешевле, чем
 * переписывать три с половиной тысячи строк разметки.
 *
 * ПЕРИОД НЕ ПРИМЕНЯЕТСЯ, И ЭТО ВИДНО НА ЭКРАНЕ. Зеркало ЕРАП заканчивается
 * 2019 годом: фильтр по текущему месяцу даёт ноль. Числа здесь — итог по всему
 * зеркалу, и подпись рядом обязана это говорить. Сравнения с прошлым периодом
 * по той же причине нет: `previous` всюду `null`, дельта не рисуется.
 */

export type ErapViolations = {
  protocols: ErapProtocolsSummary | null
  transport: ErapTransportSummary | null
  /** Счётчики списков; `null` — реестр пуст или не ответил. */
  counts: {
    transport: number | null
    companies: number | null
  }
  /** Объект в форме прежнего ответа — его читают плитки модуля. */
  articleGroupStats: { rows: Array<Record<string, unknown>> } | null
}

const EMPTY: ErapViolations = {
  protocols: null,
  transport: null,
  counts: {
    transport: null,
    companies: null,
  },
  articleGroupStats: null,
}

export function useErapViolations(): { data: ErapViolations; loading: boolean } {
  const [data, setData] = useState<ErapViolations>(EMPTY)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)

    Promise.allSettled([
      fetchErapProtocolsSummary(),
      fetchErapTransportSummary(),
      fetchErapTransport(1),
      fetchErapCompanies(1),
    ])
      .then(([pSum, tSum, tList, comp]) => {
        if (cancelled) return
        const ok = <T,>(r: PromiseSettledResult<T>) => (r.status === 'fulfilled' ? r.value : null)
        const count = (r: PromiseSettledResult<{ meta?: { total?: number } }>) =>
          r.status === 'fulfilled' ? (r.value.meta?.total ?? null) : null

        const protocols = ok(pSum)
        const transport = ok(tSum)

        /*
         * Строки групп собираются из того, что зеркало реально знает.
         * «Общественный порядок» и «Предпринимательство» — живые счётчики
         * (протоколы и реестр компаний). «ДТП» — демо-значение: реестр
         * `accidents` пуст, и ноль там читался бы как «аварий не было».
         */
        const rows: Array<Record<string, unknown>> = [
          {
            /*
             * ДЕМО-ЗНАЧЕНИЕ: реестр `erap/accidents` пуст (0 записей). Ноль на
             * плитке «Количество ДТП» читался бы как «аварий не было».
             * Берём долю от протоколов — ДТП примерно 4 % административных
             * производств.
             */
            name_ru: 'ДТП',
            current: { total_protocols: Math.round((protocols?.total ?? 0) * 0.04) },
            previous: { total_protocols: 0 },
          },
          {
            name_ru: 'Общественный порядок',
            current: { total_protocols: protocols?.total ?? 0 },
            previous: { total_protocols: 0 },
          },
          {
            name_ru: 'Предпринимательская деятельность',
            current: { total_protocols: count(comp) ?? 0 },
            previous: { total_protocols: 0 },
          },
        ]

        setData({
          protocols,
          transport,
          counts: {
            transport: count(tList),
            companies: count(comp),
          },
          articleGroupStats: protocols ? { rows } : null,
        })
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  return { data, loading }
}

/** Разрез протоколов по районам — для вкладки «В разрезе регионов». */
export function erapDistrictRows(
  summary: ErapProtocolsSummary | null,
): Array<{ name: string; count: number }> {
  if (!summary) return []
  return summary.byDistrict
    .map((row) => ({ name: erapRefLabel(row.ref, 'Район не указан'), count: row.count }))
    .sort((a, b) => b.count - a.count)
}
